import { AIModule } from '@/data/ai/types';

export const DATA_PIPELINE_DEPLOYMENT_MODULE: AIModule = {
  id: 'data-pipeline-deployment',
  title: 'ML Model Deployment',
  description: 'Deploy ML models to production with CI/CD, containerization, and serving infrastructure.',
  duration: '2.5 hours',
  difficulty: 'intermediate',
  order: 4,
  content: `
## What You'll Learn
- Containerize ML models with Docker
- Deploy models as REST APIs
- Implement CI/CD for ML systems
- Scale model serving infrastructure

## Overview
Model deployment bridges the gap between experimentation and production value. Modern MLOps uses containers, APIs, and automation.

---

## Key Concepts

### 1. Model Serving with FastAPI
Create production-ready model APIs:

\`\`\`python
from fastapi import FastAPI
from pydantic import BaseModel
import joblib

app = FastAPI()
model = joblib.load('model.pkl')

class PredictRequest(BaseModel):
    features: list[float]

class PredictResponse(BaseModel):
    prediction: float
    confidence: float

@app.post("/predict", response_model=PredictResponse)
async def predict(request: PredictRequest):
    pred = model.predict([request.features])[0]
    proba = model.predict_proba([request.features])[0].max()
    return PredictResponse(prediction=pred, confidence=proba)
\`\`\`

### 2. Docker Containerization
Package models with dependencies:

\`\`\`dockerfile
FROM python:3.10-slim

WORKDIR /app
COPY requirements.txt .
RUN pip install -r requirements.txt

COPY model.pkl .
COPY app.py .

EXPOSE 8000
CMD ["uvicorn", "app:app", "--host", "0.0.0.0"]
\`\`\`

---

## Best Practices
- **Health Checks**: Add /health endpoint
- **Versioning**: Version your model APIs
- **A/B Testing**: Roll out models gradually
- **Rollback**: Keep previous versions ready

---

## Key Takeaways
✅ FastAPI provides high-performance model serving
✅ Docker ensures consistent deployments
✅ CI/CD automates the deployment process
✅ Always have rollback capabilities

## Resources
- [FastAPI Documentation](https://fastapi.tiangolo.com/)
- [Docker for ML](https://docs.docker.com/)
`,
  learningObjectives: [
    'Build model serving APIs',
    'Containerize ML models',
    'Implement CI/CD pipelines',
    'Scale model infrastructure'
  ],
  quiz: {
    questions: [
      {
        id: 'dpd-q1',
        question: 'Which framework is recommended for ML model APIs?',
        options: ['Django', 'FastAPI', 'Flask only', 'Express.js'],
        correctAnswer: 1,
        explanation: 'FastAPI is recommended for ML APIs due to its high performance and automatic documentation.'
      },
      {
        id: 'dpd-q2',
        question: 'What is the purpose of a Dockerfile?',
        options: ['Store data', 'Define container build instructions', 'Train models', 'Log metrics'],
        correctAnswer: 1,
        explanation: 'Dockerfile contains instructions to build a Docker image with all dependencies.'
      },
      {
        id: 'dpd-q3',
        question: 'What does CI/CD stand for?',
        options: ['Container Integration/Deployment', 'Continuous Integration/Continuous Deployment', 'Code Inspection/Delivery', 'Cloud Infrastructure/Development'],
        correctAnswer: 1,
        explanation: 'CI/CD stands for Continuous Integration and Continuous Deployment.'
      },
      {
        id: 'dpd-q4',
        question: 'Why add a /health endpoint?',
        options: ['For logging', 'To check if service is running', 'To train models', 'For authentication'],
        correctAnswer: 1,
        explanation: 'Health endpoints allow load balancers and orchestrators to check service status.'
      },
      {
        id: 'dpd-q5',
        question: 'What is A/B testing in ML deployment?',
        options: ['Testing two APIs', 'Comparing model versions on live traffic', 'Unit testing', 'Load testing'],
        correctAnswer: 1,
        explanation: 'A/B testing compares different model versions on live traffic to evaluate performance.'
      }
    ]
  },
  practice: {
    task: 'Create a Model API: Build a FastAPI endpoint that serves predictions from a trained model. Create a /predict endpoint with proper request/response models and add a /health GET endpoint.',
    starter: `from fastapi import FastAPI
from pydantic import BaseModel

app = FastAPI()

# TODO: Define PredictRequest with features list

# TODO: Define PredictResponse with prediction and confidence

# TODO: Create /predict POST endpoint

# TODO: Add /health GET endpoint`,
    solution: `from fastapi import FastAPI
from pydantic import BaseModel
from typing import List

app = FastAPI(title="ML Model API", version="1.0.0")

# Simulated model (replace with real model)
class SimpleModel:
    def predict(self, features):
        return sum(features) / len(features)
    
    def predict_proba(self, features):
        return 0.85

model = SimpleModel()

class PredictRequest(BaseModel):
    features: List[float]
    
    class Config:
        schema_extra = {"example": {"features": [1.0, 2.0, 3.0]}}

class PredictResponse(BaseModel):
    prediction: float
    confidence: float
    model_version: str = "1.0.0"

class HealthResponse(BaseModel):
    status: str
    model_loaded: bool

@app.post("/predict", response_model=PredictResponse)
async def predict(request: PredictRequest):
    prediction = model.predict(request.features)
    confidence = model.predict_proba(request.features)
    return PredictResponse(
        prediction=prediction,
        confidence=confidence
    )

@app.get("/health", response_model=HealthResponse)
async def health():
    return HealthResponse(
        status="healthy",
        model_loaded=model is not None
    )`,
    hints: [
      'Use Pydantic BaseModel for request/response schemas',
      'Add type hints for better validation',
      'Include model version in responses'
    ],
    estimatedTime: 30
  }
};
